import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Header from "../components/Header";
import { useNavigate } from "react-router-dom";
import { Alert } from "@mui/material";
import { loginReset } from "../store/slices/adminSlices";
import { Helmet } from "react-helmet";
import { isLocalSamlBypassEnabled } from "../utils/authBypass";

const LoginScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { adminInfo, error } = useSelector((state) => {
    return state.login;
  });

  useEffect(() => {
    if (adminInfo) {
      // Send all authenticated users to the Command Center hub.
      navigate("/command-center");
    }

    error && setTimeout(() => {
      dispatch(loginReset());
    }, 2000)
  }, [adminInfo, navigate, error, dispatch]);

  const handleSso = () => {
    // Redirect to backend SAML init endpoint; backend generates AuthnRequest to Google IdP
    const backendUrl = process.env.NODE_ENV === 'production'
      ? (process.env.REACT_APP_PRODUCTION_BASE_URL || 'https://api.bellhopt.com/api/v1')
      : (process.env.REACT_APP_DEVELOPMENT_BASE_URL || 'http://localhost:5001/api/v1');
    const samlLoginUrl = `${String(backendUrl).trim().replace(/\/$/, '')}/saml/login`;
    window.location.href = samlLoginUrl;
  };

  const handleLocalBypass = () => {
    navigate("/command-center");
  };

  const bypassEnabled = isLocalSamlBypassEnabled();

  return (
    <>
      <div className="flex flex-col min-h-screen w-full overflow-y-auto">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href="https://bellhopt.com" />
        </Helmet>
        <Header />
        {error && (
          <Alert
            severity="error"
            sx={{
              position: "fixed",
              width: "90%",
              margin: "auto",
              top: 20,
              left: 0,
              right: 0,
              zIndex: 10,
            }}
          >
            <strong>{error}</strong>
          </Alert>
        )}

        <div className="flex justify-center items-center bg-white h-screen w-full flex-grow">
          <div className="laptop:w-[480px] tabletOnly:w-[420px] mobile:w-[340px] text-center">
            {/* TOP */}
            <div className="top mb-8">
              <h2 className="font-bold text-2xl text-gray-900">Welcome Back</h2>
              <p className="text-sm text-gray-500 mt-2">Sign in to access the Operations Dashboard</p>
            </div>

            {/* SSO CTA */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={handleSso}
                className="w-full inline-flex items-center justify-center gap-3 h-[56px] rounded-xl bg-black text-white font-semibold hover:bg-zinc-800 transition-colors shadow-lg"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="text-sm">Sign in with Google SSO</span>
              </button>
              {bypassEnabled && (
                <button
                  type="button"
                  onClick={handleLocalBypass}
                  className="w-full inline-flex items-center justify-center h-[48px] rounded-xl bg-amber-100 text-amber-900 font-semibold hover:bg-amber-200 transition-colors border border-amber-300"
                >
                  Continue in Local Bypass Mode
                </button>
              )}
              <p className="text-xs text-gray-400">
                {bypassEnabled
                  ? "Local bypass is ON (localhost only). Turn off REACT_APP_LOCAL_SAML_BYPASS to restore SSO."
                  : "Use your Bellhopt Google Workspace account"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginScreen;
